import React from 'react';
import styled, { keyframes } from 'styled-components';
import { TableHeader, TableBody, TableRow } from '@/components/ui/table';
import {
  StyledTableShell,
  StyledTable,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';
import { CardContent } from '@/components/ui/card';

/**
 * Shimmer animation for skeleton loaders
 */
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

/**
 * Pulse animation for reduced motion preference
 */
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

/**
 * Base skeleton element with shimmer effect
 */
const SkeletonBase = styled.div<{ width?: string; height?: string }>`
  background: linear-gradient(
    90deg,
    #eef2f6 0px,
    #f7f9fc 40px,
    #eef2f6 80px
  );
  background-size: 1000px 100%;
  border-radius: 4px;
  animation: ${shimmer} 2s infinite linear;
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '16px'};

  @media (prefers-reduced-motion: reduce) {
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

const SkeletonRow = styled(TableRow)`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-left: 4px solid rgba(148, 163, 184, 0.3);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);

  td:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }

  td:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const SkeletonCell = styled(StyledTableCell)`
  padding: 1rem 0.85rem;
`;

const SkeletonBadge = styled(SkeletonBase)`
  width: 80px;
  height: 24px;
  border-radius: 999px;
  margin: 0 auto;
`;

interface RolesTableSkeletonProps {
  /**
   * Number of skeleton rows to display
   */
  rows?: number;
}

/**
 * Skeleton loader for the roles table
 * Displays animated placeholder rows while data is loading
 */
export function RolesTableSkeleton({
  rows = 5,
}: RolesTableSkeletonProps) {
  const headers = [
    'Client',
    'External ID',
    'Job Title',
    'Industry',
    'Location',
    'Type',
    'Audience',
    'Matches',
    'Status',
    'Actions',
  ];

  return (
    <StyledTableShell>
      <CardContent>
        <StyledTable>
          <TableHeader>
            <TableRow>
              {headers.map((header, idx) => (
                <StyledTableHead
                  key={header}
                  className={idx >= headers.length - 4 ? 'text-center' : ''}
                >
                  {header}
                </StyledTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, rowIdx) => (
              <SkeletonRow key={rowIdx}>
                {/* Client */}
                <SkeletonCell>
                  <SkeletonBase width="85%" height="16px" />
                </SkeletonCell>
                {/* External ID */}
                <SkeletonCell>
                  <SkeletonBase width="70%" height="16px" />
                </SkeletonCell>
                {/* Job Title */}
                <SkeletonCell>
                  <SkeletonBase width="90%" height="16px" />
                </SkeletonCell>
                {/* Industry */}
                <SkeletonCell>
                  <SkeletonBase width="75%" height="16px" />
                </SkeletonCell>
                {/* Location */}
                <SkeletonCell>
                  <SkeletonBase width="80%" height="16px" />
                </SkeletonCell>
                {/* Type */}
                <SkeletonCell className="text-center">
                  <SkeletonBadge />
                </SkeletonCell>
                {/* Audience */}
                <SkeletonCell className="text-center">
                  <SkeletonBadge />
                </SkeletonCell>
                {/* Matches */}
                <SkeletonCell className="text-center">
                  <SkeletonBase width="40px" height="24px" style={{ margin: '0 auto', borderRadius: '999px' }} />
                </SkeletonCell>
                {/* Status */}
                <SkeletonCell className="text-center">
                  <SkeletonBadge />
                </SkeletonCell>
                {/* Actions */}
                <SkeletonCell className="text-center">
                  <SkeletonBase width="80px" height="32px" style={{ margin: '0 auto' }} />
                </SkeletonCell>
              </SkeletonRow>
            ))}
          </TableBody>
        </StyledTable>
      </CardContent>
    </StyledTableShell>
  );
}

export default RolesTableSkeleton;
